import { VtoProduct } from "@hermes/api-model-virtual-try-on";

import {
    LocalizedVtoCategory,
    LocalizedVtoSubcategory,
    NO_CATEGORY,
    NO_SELECT,
    NO_SHADE,
    NO_SUBCATEGORY,
    SkuApplied,
} from "../model/localized-vto-category.model";
import { VtoState } from "../reducers/vto-state";

/**
 * Reset all categories with initial skuModal value
 */
export const resetSelectedVariantInSubcategories = (
    localizedVtoCategory: LocalizedVtoCategory[],
): LocalizedVtoCategory[] =>
    localizedVtoCategory.map((category) => {
        category.selectedSubcategoryType = NO_SELECT;
        const updatedSubcategories = category.vtoSubcategories.map(
            (subCategory) => {
                subCategory.selectedSkuVariant = NO_SELECT;
                return subCategory;
            },
        );
        return { ...category, vtoSubcategories: updatedSubcategories };
    });

export const updateCategories = (
    categories: LocalizedVtoCategory[],
    activeCategory: LocalizedVtoCategory,
    activeSubCategory: LocalizedVtoSubcategory,
): LocalizedVtoCategory[] =>
    categories.map((category) => {
        if (category.vtoCategoryType === activeCategory.vtoCategoryType) {
            const updatedSubcategories = category.vtoSubcategories.map(
                (subcategory) => {
                    if (
                        subcategory.vtoSubcategoryType ===
                        activeSubCategory.vtoSubcategoryType
                    ) {
                        return activeSubCategory;
                    }
                    return subcategory;
                },
            );
            return {
                ...activeCategory,
                vtoSubcategories: updatedSubcategories,
            };
        }
        return category;
    });

export const prepareCategories = (
    categories: LocalizedVtoCategory[],
    sku: string,
): Pick<
    VtoState,
    "activeCategory" | "activeSubcategory" | "localizedVtoCategories"
> => {
    const activeCategory = getActiveCategory(categories, sku);
    const activeSubcategory = getActiveSubcategoryBySku(activeCategory, sku);
    activeSubcategory.selectedSkuVariant = sku;
    activeCategory.selectedSubcategoryType =
        activeSubcategory.localizedVtoSubcategoryType;
    return {
        localizedVtoCategories: updateCategories(
            categories,
            activeCategory,
            activeSubcategory,
        ),
        activeCategory,
        activeSubcategory,
    };
};

export const getActiveCategory = (
    categories: LocalizedVtoCategory[],
    sku: string,
): LocalizedVtoCategory =>
    categories.find((category) =>
        category.vtoSubcategories.find((subCategory) =>
            subCategory.vtoProducts.find((product) => product.sku === sku),
        ),
    ) ?? NO_CATEGORY;

/**
 * Gets the active subcategory based on the provided active category.
 * If no shade is selected, find the subcategory based on the selected subcategory type.
 * If a shade is selected, find the subcategory containing the selected variant category.
 * @param activeCategory
 * @returns  active subcategory.
 */
export const getVtoSubcategoryByType = (
    activeCategory: LocalizedVtoCategory,
): LocalizedVtoSubcategory => {
    const activeSubcategory =
        activeCategory.selectedSubcategoryType === NO_SELECT
            ? activeCategory.vtoSubcategories[0]
            : activeCategory.vtoSubcategories.find(
                  (subCategory) =>
                      subCategory.localizedVtoSubcategoryType ===
                      activeCategory.selectedSubcategoryType,
              ) ?? NO_SUBCATEGORY;
    if (activeSubcategory.selectedSkuVariant === NO_SELECT) {
        activeSubcategory.selectedSkuVariant =
            activeSubcategory.vtoProducts[0].sku;
    }
    return activeSubcategory;
};

export const getActiveSubcategoryBySku = (
    activeCategory: LocalizedVtoCategory,
    sku: string,
): LocalizedVtoSubcategory =>
    activeCategory.vtoSubcategories.find((subCategory) =>
        subCategory.vtoProducts.find((product) => product.sku === sku),
    ) ?? NO_SUBCATEGORY;

/**
 * Check initial state sku
 */
export const isSku = (sku: string): boolean =>
    sku !== NO_SELECT && sku !== NO_SHADE && sku !== "";

/**
 * find VtoProduct from list
 */
export const getProductVariantFromList = (
    vtoProducts: VtoProduct[],
    sku: string,
): VtoProduct | undefined =>
    vtoProducts.find((vtoProduct) => vtoProduct.sku === sku);

/**
 * find VtoProduct Index from list
 */
export const getProductIndexfromList = (
    vtoProducts: VtoProduct[],
    sku: string,
): number => vtoProducts.findIndex((vtoProduct) => vtoProduct.sku === sku);

/**
 * return params form vtoProduct url (product, variant, sku)
 */
export const getRouteParametersWithoutLocation = (url: string): string[] => {
    const routeParameters = url.split("/");
    return routeParameters.slice(3, routeParameters.length);
};

/*
 * find vtoProduct from category
 * check is in stock product
 */
export const getLearnMoreDisplay = (
    vtoSubcategories: LocalizedVtoSubcategory,
    currentVtoSku: string,
): boolean => {
    let selectedProduct: VtoProduct | undefined;
    if (vtoSubcategories && isSku(currentVtoSku)) {
        selectedProduct = vtoSubcategories.vtoProducts.find(
            (product: VtoProduct) => product.sku === currentVtoSku,
        );
    }
    return selectedProduct ? selectedProduct.stock : false;
};

/*
 * check if vtoProduct is not undefined
 */
export const isVtoProductNotUndefined = (
    vtoProduct: VtoProduct | undefined,
): vtoProduct is VtoProduct => vtoProduct !== undefined;

export const findVtoProductFromSku = (
    skuToFind: string,
    categories: LocalizedVtoCategory[],
): VtoProduct | undefined => {
    let productSelected: VtoProduct | undefined;
    // We must search in all categories / subcategories as currentVtoSku can be in
    // another category than activeCategory.
    categories.forEach((localizedVtoCategory: LocalizedVtoCategory) =>
        localizedVtoCategory.vtoSubcategories.forEach(
            (vtoSubcategory: LocalizedVtoSubcategory) => {
                const productFound = vtoSubcategory.vtoProducts.find(
                    (vtoProduct: VtoProduct) => vtoProduct.sku === skuToFind,
                );
                if (productFound) {
                    productSelected = productFound;
                }
            },
        ),
    );
    return productSelected;
};

export const getFirstSkuProductFromCategories = (
    categories: LocalizedVtoCategory[],
): string => categories[0].vtoSubcategories[0]?.vtoProducts[0]?.sku;

export const getUpdatedSkuApplied = (
    skuApplied: SkuApplied,
    skuType: string,
    selectedSku: string,
): SkuApplied =>
    skuType === NO_SELECT
        ? skuApplied
        : {
              ...skuApplied,
              [skuType]: selectedSku,
          };

export const updatedSubcategoryOnActiveCategory = (
    activeCategory: LocalizedVtoCategory,
    activeSubcategory: LocalizedVtoSubcategory,
): LocalizedVtoCategory => {
    const updatedSubcategories = activeCategory.vtoSubcategories.map(
        (subcategory) =>
            subcategory.vtoSubcategoryType ===
            activeSubcategory.vtoSubcategoryType
                ? activeSubcategory
                : subcategory,
    );

    return {
        ...activeCategory,
        vtoSubcategories: updatedSubcategories,
    };
};
